import type { Address } from '../../api';
import type { RosterFacility, RosterProvider, RosterRole, RosterTab } from './roster.types';

/**
 * The Roster table's columns, as the design lists them (FE-109).
 *
 * `opsOnly` columns exist only for ENTER ops. They are removed for a client admin rather than
 * hidden, so they never appear in the column picker or in a CSV the client downloads.
 */

export type RosterCellKind = 'link' | 'tag' | 'num' | 'text';

export interface RosterColumn {
    key: string;
    label: string;
    cell: RosterCellKind;
    opsOnly?: boolean;
    /** Shown until the user changes the column picker. */
    defaultVisible?: boolean;
}

export const ROSTER_PROVIDER_COLUMNS: RosterColumn[] = [
    { key: 'name', label: 'Name', cell: 'link', defaultVisible: true },
    { key: 'type', label: 'Type', cell: 'tag', defaultVisible: true },
    { key: 'npi', label: 'NPI', cell: 'num', defaultVisible: true },
    { key: 'organization', label: 'Organization', cell: 'text', defaultVisible: true },
    { key: 'tags', label: 'Tags', cell: 'text', defaultVisible: true },
    { key: 'updated', label: 'Updated', cell: 'text', defaultVisible: true },
    { key: 'email', label: 'Email', cell: 'text' },
    { key: 'taxonomyCode', label: 'Taxonomy Code', cell: 'num' },
    { key: 'ein', label: 'EIN', cell: 'num' },
    { key: 'ptan', label: 'PTAN', cell: 'num' },
    { key: 'license', label: 'License', cell: 'text' },
    { key: 'automation', label: 'Automation', cell: 'text', opsOnly: true },
    { key: 'halt', label: 'Halt', cell: 'tag', opsOnly: true },
    { key: 'id', label: 'Id', cell: 'num', opsOnly: true },
];

export const ROSTER_FACILITY_COLUMNS: RosterColumn[] = [
    { key: 'name', label: 'Name', cell: 'link', defaultVisible: true },
    { key: 'npi', label: 'NPI', cell: 'num', defaultVisible: true },
    { key: 'placeOfService', label: 'Place of Service', cell: 'text', defaultVisible: true },
    { key: 'address', label: 'Primary Address', cell: 'text', defaultVisible: true },
    { key: 'updated', label: 'Updated', cell: 'text', defaultVisible: true },
    { key: 'nickname', label: 'Nickname', cell: 'text' },
    { key: 'ein', label: 'EIN', cell: 'num' },
    { key: 'other', label: 'Other ID', cell: 'num', opsOnly: true },
    { key: 'id', label: 'Id', cell: 'num', opsOnly: true },
];

export interface RosterColumnOptions {
    /**
     * Whether the source has a real "updated" time. The support API does not (checked 2026-09-25):
     * a column of dashes would read as "never updated", so without it the column is left out
     * rather than shown empty (NS-3, NS-4).
     */
    updatedTimestamp?: boolean;
    /** `false` when the source has no tags — the Tags column is left out, as for `updatedTimestamp`. */
    tags?: boolean;
    /** Tagging that is not available yet means records come without tags: the column is left out too. */
    comingSoon?: ReadonlyArray<string>;
}

export function rosterColumnsFor(tab: RosterTab, role: RosterRole, options: RosterColumnOptions = {}): RosterColumn[] {
    const all = tab === 'providers' ? ROSTER_PROVIDER_COLUMNS : ROSTER_FACILITY_COLUMNS;
    return all.filter(
        column =>
            (role === 'ops' || !column.opsOnly) &&
            (column.key !== 'updated' || options.updatedTimestamp !== false) &&
            (column.key !== 'tags' || (options.tags !== false && !options.comingSoon?.includes('tag'))),
    );
}

export function rosterDefaultVisibleKeys(
    tab: RosterTab,
    role: RosterRole,
    options: RosterColumnOptions = {},
): string[] {
    return rosterColumnsFor(tab, role, options)
        .filter(column => column.defaultVisible)
        .map(column => column.key);
}

/**
 * First and last name when there is one, the organization otherwise.
 *
 * Same order as `ProvidersStore.getDisplayName` in the support app, so a provider carries the
 * same name on this screen as everywhere else there. The id fallback matches it too: a record
 * with neither is a data problem, and showing its id makes it findable instead of blank.
 */
export function rosterProviderName(provider: Pick<RosterProvider, 'id' | 'first' | 'last' | 'organization'>): string {
    const person = [provider.first, provider.last].filter(Boolean).join(' ');
    return person || provider.organization || provider.id;
}

/** `lines…, city, state, postal, country` — the order the support Facilities list uses. */
export function rosterAddressText(address?: Address): string {
    if (!address) {
        return '';
    }
    return [...(address.lines || []), address.city, address.state, address.postal, address.country]
        .filter(Boolean)
        .join(', ');
}

/**
 * The plain text of one provider cell. Search, sort and CSV all read this, so the three agree.
 *
 * `updated` stays ISO so it sorts chronologically; the UI formats it for display. An empty
 * string means no value — the UI decides how absence reads.
 */
export function rosterProviderCellText(provider: RosterProvider, key: string): string {
    switch (key) {
        case 'name':
            return rosterProviderName(provider);
        case 'tags':
            return provider.tags.map(tag => tag.name).join(', ');
        case 'automation':
            if (!provider.automation) {
                return '';
            }
            return `Professionals ${provider.automation.professional ? 'On' : 'Off'} · Payments ${
                provider.automation.payment ? 'On' : 'Off'
            }`;
        case 'halt':
            return provider.halt ? 'HALTED' : '';
        case 'address':
            return rosterAddressText(provider.addresses[0]);
        default:
            return stringField(provider as unknown as Record<string, unknown>, key);
    }
}

/** See `rosterProviderCellText`. `placeOfService` is the raw enum; the UI turns it into a CMS code and label. */
export function rosterFacilityCellText(facility: RosterFacility, key: string): string {
    switch (key) {
        case 'placeOfService':
            return facility.placeOfServiceCode || '';
        case 'address':
            return rosterAddressText(facility.addresses[0]);
        case 'tags':
            return facility.tags.map(tag => tag.name).join(', ');
        default:
            return stringField(facility as unknown as Record<string, unknown>, key);
    }
}

function stringField(record: Record<string, unknown>, key: string): string {
    const value = record[key];
    return value === undefined || value === null ? '' : String(value);
}
